import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { Driver, FleetVehicle } from '../types';
import { driverAPI } from '../services/api';

interface Props {
  open: boolean;
  driver: Driver | null;
  vehicles: FleetVehicle[];
  onClose: () => void;
  onAssigned: () => void;
}

const AssignVehicleDialog: React.FC<Props> = ({ open, driver, vehicles, onClose, onAssigned }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setSelectedVehicleId('');
      setIsPrimary(false);
      setError('');
    }
  }, [open]);

  // Debug logging
  React.useEffect(() => {
    if (open) {
      console.log('=== AssignVehicleDialog Debug ===');
      console.log('Driver:', driver?.firstName, driver?.lastName);
      console.log('All vehicles count:', vehicles.length);
    }
  }, [open, driver, vehicles]);

  const handleAssign = async () => {
    if (!driver || !selectedVehicleId) return;

    setLoading(true);
    setError('');

    try {
      await driverAPI.assignVehicle(driver._id, selectedVehicleId, isPrimary);
      setSelectedVehicleId('');
      setIsPrimary(false);
      onAssigned();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to assign vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async (vehicleId: string) => {
    if (!driver) return;

    setLoading(true);
    setError('');

    try {
      await driverAPI.unassignVehicle(driver._id, vehicleId);
      onAssigned();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to unassign vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (vehicleId: string) => {
    if (!driver) return;

    setLoading(true);
    setError('');

    try {
      await driverAPI.setPrimaryVehicle(driver._id, vehicleId);
      onAssigned();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to set primary vehicle');
    } finally {
      setLoading(false);
    }
  };

  // Safely extract vehicle IDs
  const assignedVehicleIds = (driver?.assignedVehicles || [])
    .map(av => {
      if (!av || !av.vehicleId) return null;
      if (typeof av.vehicleId === 'object' && av.vehicleId !== null) {
        return av.vehicleId.id || (av.vehicleId as any)._id || null;
      }
      if (typeof av.vehicleId === 'string') {
        return av.vehicleId;
      }
      return null;
    })
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

  // Filter out vehicles assigned to other drivers
  const availableVehicles = vehicles.filter(v =>
    !v.assignedDriver ||
    assignedVehicleIds.includes(v.id)
  );

  const currentVehicles = driver?.assignedVehicles || [];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}  // ✅ Full screen on mobile
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : '90vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <CarIcon color="primary" sx={{ fontSize: isMobile ? 20 : 24 }} />
            <Typography variant={isMobile ? 'h6' : 'h5'}>
              {isMobile ? 'Manage Vehicles' : 'Manage Vehicle Assignments'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size={isMobile ? 'small' : 'medium'}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {driver && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Driver
            </Typography>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
              {driver.firstName} {driver.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {driver.email} • {driver.licenseNumber}
            </Typography>
          </Box>
        )}

        {/* Currently Assigned Vehicles */}
        {currentVehicles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assigned Vehicles ({currentVehicles.length})
            </Typography>
            <List sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
              {currentVehicles
                .map((assignment, index) => {
                  let vehicle: FleetVehicle | undefined;

                  if (typeof assignment.vehicleId === 'object' && assignment.vehicleId !== null) {
                    vehicle = assignment.vehicleId as FleetVehicle;
                    if (!vehicle.id && (vehicle as any)._id) {
                      vehicle = { ...vehicle, id: (vehicle as any)._id };
                    }
                  } else if (typeof assignment.vehicleId === 'string') {
                    vehicle = vehicles.find(v => v.id === assignment.vehicleId || (v as any)._id === assignment.vehicleId);
                  }

                  return { assignment, vehicle, index };
                })
                .filter((item): item is { assignment: any; vehicle: FleetVehicle; index: number } =>
                  item.vehicle !== undefined &&
                  item.vehicle.id !== undefined &&
                  item.vehicle.plateNumber !== undefined
                )
                .map(({ assignment, vehicle, index }) => (
                  <React.Fragment key={vehicle.id}>
                    {index > 0 && <Divider />}
                    <ListItem 
                      sx={{ 
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        py: isMobile ? 2 : 1
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack spacing={0.5}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body1" fontWeight="medium">
                                {vehicle.plateNumber}
                              </Typography>
                              {assignment.isPrimary && (
                                <Chip
                                  size="small"
                                  label="Primary"
                                  color="primary"
                                  icon={<StarIcon />}
                                />
                              )}
                            </Box>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              <Chip
                                size="small"
                                label={`${vehicle.make} ${vehicle.model} (${vehicle.year})`}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={vehicle.category.replace('_', ' ')}
                                color="info"
                                variant="outlined"
                              />
                            </Box>
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            VIN: {vehicle.vin || 'N/A'} • Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction
                        sx={{
                          position: isMobile ? 'relative' : 'absolute',
                          right: isMobile ? 'auto' : 16,
                          top: isMobile ? 'auto' : '50%',
                          transform: isMobile ? 'none' : 'translateY(-50%)',
                          display: 'flex',
                          gap: 0.5,
                          mt: isMobile ? 1 : 0,
                          width: isMobile ? '100%' : 'auto',
                          justifyContent: isMobile ? 'flex-end' : 'flex-start'
                        }}
                      >
                        {!assignment.isPrimary && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetPrimary(vehicle.id);
                            }}
                            disabled={loading}
                            title="Set as primary"
                          >
                            <StarBorderIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (currentVehicles.length === 1) {
                              const confirmed = window.confirm(
                                'This will remove the last vehicle from this driver.\n\nContinue?'
                              );
                              if (confirmed) {
                                handleUnassign(vehicle.id);
                              }
                            } else {
                              handleUnassign(vehicle.id);
                            }
                          }}
                          disabled={loading}
                          title="Remove vehicle"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Assign New Vehicle */}
        <Typography variant="subtitle2" gutterBottom>
          Assign Additional Vehicle
        </Typography>
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            label="Select Vehicle"
            value={selectedVehicleId}
            onChange={(e) => {
              setSelectedVehicleId(e.target.value);
            }}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            SelectProps={{
              native: false,
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: isMobile ? 300 : 400,
                  },
                },
              },
            }}
          >
            <MenuItem value="">
              <em>Select a vehicle</em>
            </MenuItem>
            {availableVehicles
              .filter(v => !assignedVehicleIds.includes(v.id))
              .map((vehicle) => (
                <MenuItem key={vehicle.id || (vehicle as any)._id} value={vehicle.id || (vehicle as any)._id}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {vehicle.plateNumber} - {vehicle.make} {vehicle.model} ({vehicle.year})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      VIN: {vehicle.vin || 'N/A'} • {vehicle.category.replace('_', ' ')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
          </TextField>
          
          <FormControlLabel
            control={
              <Switch
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                disabled={loading || !selectedVehicleId}
              />
            }
            label="Set as Primary Vehicle"
          />
        </Stack>

        {availableVehicles.filter(v => !assignedVehicleIds.includes(v.id)).length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No available vehicles. All vehicles are currently assigned.
          </Alert>
        )}

        {isPrimary && selectedVehicleId && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will become the driver's primary vehicle.
          </Alert>
        )}
      </DialogContent>

      <DialogActions 
        sx={{ 
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: isMobile ? 1 : 0,
          p: isMobile ? 2 : 1,
          '& > button': {
            width: isMobile ? '100%' : 'auto'
          }
        }}
      >
        <Button 
          onClick={onClose} 
          disabled={loading}
          fullWidth={isMobile}
          variant={isMobile ? 'outlined' : 'text'}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={loading || !selectedVehicleId}
          startIcon={!isMobile && <CarIcon />}
          fullWidth={isMobile}
        >
          {loading ? 'Assigning...' : 'Assign Vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignVehicleDialog;